import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmClient } from '../crm/crm.model';
import { CrmService } from '../crm/crm.service';
import { statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from './warehousing-common';
import { WarehousingBillingDraft, WarehousingDestruction, WarehousingDispatch, WarehousingRefurbishment, WarehousingStockSummary } from './warehousing.model';
import { WarehousingService } from './warehousing.service';
import { itemSizeLabel } from './warehousing-item-display';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { WarehousingPageHeaderComponent } from './warehousing-page-header.component';

@Component({
  selector: 'app-warehousing-workspace',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    TableModule,
    TagModule,
    ToastModule,
    WarehousingPageHeaderComponent,
    PageMetricStripComponent
  ],
  templateUrl: './warehousing-workspace.component.html',
  styleUrl: './warehousing-workspace.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WarehousingWorkspaceComponent implements OnInit {
  readonly itemSizeLabel = itemSizeLabel;
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);

  section = signal('overview');
  loading = signal(false);
  summaries = signal<WarehousingStockSummary[]>([]);
  billingDrafts = signal<WarehousingBillingDraft[]>([]);
  dispatches = signal<WarehousingDispatch[]>([]);
  refurbishments = signal<WarehousingRefurbishment[]>([]);
  destructions = signal<WarehousingDestruction[]>([]);
  clients = signal<CrmClient[]>([]);
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Overview');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  totals = computed(() => {
    const summaries = this.summaries();
    const billingDrafts = this.billingDrafts();
    const dispatches = this.dispatches();
    const refurbishments = this.refurbishments();
    const destructions = this.destructions();
    const onHand = summaries.reduce((sum, row) => sum + (row.onHandQuantity || 0), 0);
    const available = summaries.reduce((sum, row) => sum + (row.availableQuantity || 0), 0);
    const reserved = summaries.reduce((sum, row) => sum + (row.reservedQuantity || 0), 0);
    const activeDispatches = dispatches.filter(row => !this.isClosedStatus(row.status));
    const activeRefurbishments = refurbishments.filter(row => !this.isClosedStatus(row.status));
    const openDestructions = destructions.filter(row => !this.isClosedStatus(row.status));
    const draftBillingAmount = billingDrafts
      .filter(row => (row.status || '').toUpperCase() === 'DRAFT')
      .reduce((sum, row) => sum + (row.totalEditedAmount || row.totalCalculatedAmount || 0), 0);

    return {
      onHand,
      available,
      reserved,
      availabilityRate: onHand ? available / onHand * 100 : 0,
      reservationRate: onHand ? reserved / onHand * 100 : 0,
      activeSkus: new Set(summaries.filter(row => (row.onHandQuantity || 0) > 0).map(row => row.itemId)).size,
      warehouseCount: new Set(summaries.filter(row => row.warehouseId).map(row => row.warehouseId)).size,
      zoneCount: new Set(summaries.filter(row => row.zoneId).map(row => row.zoneId)).size,
      openDispatches: activeDispatches.length,
      outboundLines: activeDispatches.reduce((sum, row) => sum + (row.lines?.length || 0), 0),
      refurbishmentWip: activeRefurbishments.length,
      destructionQueue: openDestructions.length,
      destroyedQtyPending: openDestructions.reduce((sum, row) => sum + (row.quantityDestroyed || 0), 0),
      draftBillingAmount,
      billingCurrency: billingDrafts.find(row => row.currency)?.currency || 'SAR',
      sqm: summaries.reduce((sum, row) => sum + (row.occupiedSqm || 0), 0),
      cbm: summaries.reduce((sum, row) => sum + (row.occupiedCbm || 0), 0)
    };
  });

  metrics = computed<PageMetric[]>(() => {
    const totals = this.totals();
    return [
      { label: 'On hand', value: this.formatNumber(totals.onHand, 2) },
      { label: 'Available', value: this.formatNumber(totals.available, 2), hint: `${this.formatNumber(totals.availabilityRate, 1)}% availability`, tone: 'success' },
      { label: 'Available %', value: `${this.formatNumber(totals.availabilityRate, 1)}%`, tone: 'success' },
      { label: 'Reserved', value: this.formatNumber(totals.reserved, 2), hint: `${this.formatNumber(totals.reservationRate, 1)}% reservation`, tone: 'warn' },
      { label: 'Reserved %', value: `${this.formatNumber(totals.reservationRate, 1)}%`, tone: 'warn' },
      { label: 'Active SKUs', value: this.formatNumber(totals.activeSkus, 0) },
      { label: 'Warehouses', value: this.formatNumber(totals.warehouseCount, 0), hint: `${this.formatNumber(totals.zoneCount, 0)} active zones` },
      { label: 'Open Dispatches', value: this.formatNumber(totals.openDispatches, 0), hint: `${this.formatNumber(totals.outboundLines, 0)} outbound lines`, tone: 'info' },
      { label: 'Refurbishment WIP', value: this.formatNumber(totals.refurbishmentWip, 0), tone: 'warn' },
      { label: 'Destruction Queue', value: this.formatNumber(totals.destructionQueue, 0), hint: `${this.formatNumber(totals.destroyedQtyPending, 2)} pending qty`, tone: 'danger' },
      { label: 'Occupied SQM', value: this.formatNumber(totals.sqm, 4) },
      { label: 'Occupied CBM', value: this.formatNumber(totals.cbm, 4) },
      { label: 'Draft Billing', value: this.formatNumber(totals.draftBillingAmount, 2), hint: totals.billingCurrency }
    ];
  });

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loading.set(true);
    forkJoin({
      clients: this.crmService.getClients(),
      summaries: this.warehousingService.getStockSummary(),
      dispatches: this.warehousingService.getDispatches(),
      refurbishments: this.warehousingService.getRefurbishments(),
      destructions: this.warehousingService.getDestructions(),
      billingDrafts: this.warehousingService.getBillingDrafts()
    }).pipe(finalize(() => this.loading.set(false)))
      .subscribe(response => {
        this.clients.set(response.clients || []);
        this.summaries.set(response.summaries.data || []);
        this.dispatches.set(response.dispatches.data || []);
        this.refurbishments.set(response.refurbishments.data || []);
        this.destructions.set(response.destructions.data || []);
        this.billingDrafts.set(response.billingDrafts.data || []);
      });
  }

  clientName(id?: number): string {
    return this.clients().find(client => client.id === id)?.companyName || '-';
  }

  statusSeverity = statusSeverity;

  private isClosedStatus(status?: string): boolean {
    return ['CANCELLED', 'COMPLETED', 'CONFIRMED', 'DISPATCHED', 'DELIVERED', 'EXITED', 'POSTED'].includes((status || '').toUpperCase());
  }

  private formatNumber(value: number, maximumFractionDigits: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: maximumFractionDigits > 0 ? 0 : 0,
      maximumFractionDigits
    }).format(value || 0);
  }
}
